/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useContext } from 'react';
import { Form } from 'react-final-form';
import { map, noop } from 'lodash';
import { css, StyleSheet } from 'aphrodite';

import { BlendBridge } from 'blb-types/blendBridge';

import { DocUploadTask } from './DocUploadTask';
import { generateTaskDescriptionKey, TaskDescription } from '../TaskDescription';
import { MeContext } from 'blb-contexts/Me';
import ClosingType = EClose.ClosingType;

const styles = StyleSheet.create({
  spacer: {
    ':not(:last-child)': {
      marginBottom: 32,
    },
  },
});

const getInitialValue = (
  taskDescription: BlendBridge.API.Task['taskDescription'],
  isRonClosing = false,
  isSettlementAgent = false,
) => {
  // assuming there is at most 1 dropdown for a given task since that is our most complex use case as of now
  const descriptionWithOptions = taskDescription?.find(
    descriptor => descriptor.type === BlendBridge.API.TaskDescriptionTypes.INPUT && descriptor.options,
  ) as BlendBridge.API.InputTaskDescription;
  // If there are no options, that means we determine documentType on the BE (for now re: BRG-567)
  // TODO: remove after BRG-567 - where all upload tasks should come with a documentType in the display config
  if (descriptionWithOptions) {
    if (
      isRonClosing &&
      isSettlementAgent &&
      descriptionWithOptions.options &&
      descriptionWithOptions.options?.length > 1
    ) {
      const titleDocumentIndex = descriptionWithOptions.options.findIndex(option => option.value === 'TITLE_DOCUMENT');

      if (titleDocumentIndex && titleDocumentIndex !== -1) {
        const titleDocument = descriptionWithOptions.options.splice(titleDocumentIndex, 1);
        descriptionWithOptions.options.unshift(titleDocument[0]);
      }
    }
    return descriptionWithOptions.options![0].value;
  }
};

export namespace OptionalUploadTask {
  export interface Props extends DocUploadTask.Props {
    taskSubheader?: string;
    closingType: ClosingType | undefined;
  }
}

export const OptionalUploadTask: React.FC<OptionalUploadTask.Props> = ({
  uploadLimit,
  disabledReason,
  displayType,
  isDisabled,
  taskHeader,
  onSuccess,
  sourceType,
  taskDescription,
  taskSubheader,
  allowedFileExtensions,
  closingType,
}) => {
  const me = useContext(MeContext);

  const prompt = (() =>
    taskDescription && (
      <>
        {map(taskDescription, descriptor => {
          const key = generateTaskDescriptionKey(descriptor);
          return <TaskDescription className={css(styles.spacer)} key={key} taskDescription={descriptor} />;
        })}
      </>
    ))();

  // Though we map through multiple task descriptor objects, there is at most
  // only 1 descriptor objects representing a dropdown for a given optional
  // document upload task. The rest of the descriptors in a taskDescription are
  // text.
  // If the number of inputs increases, this component will require an update.
  return (
    <Form
      initialValues={{
        selectedDocumentType: getInitialValue(
          taskDescription,
          closingType === 'RON',
          me?.principalType === 'settlement_agent',
        ),
      }}
      onSubmit={noop}
    >
      {({ form }) => {
        const { selectedDocumentType } = form.getState().values;
        return (
          <DocUploadTask
            allowedFileExtensions={allowedFileExtensions}
            disabledReason={disabledReason}
            displayType={displayType}
            documentType={selectedDocumentType}
            isDisabled={isDisabled}
            key={taskHeader}
            onSuccess={onSuccess}
            prompt={prompt}
            sourceType={sourceType}
            taskHeader={taskHeader}
            taskSubheader={taskSubheader}
            uploadLimit={uploadLimit}
          />
        );
      }}
    </Form>
  );
};
